<script setup>
import { computed, ref, watch } from 'vue';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import fromUnixTime from 'date-fns/fromUnixTime';
import ReportsAPI from 'dashboard/api/reports';
import { pickScopeFilters } from 'dashboard/api/buildSummaryReportParams';
import { clampDataBetweenTimeline } from 'shared/helpers/ReportsDataHelper';
import { useSummaryReportArchive } from '../composables/useSummaryReportArchive';
import { buildHourArchiveFilters } from '../helpers/summaryReportArchiveHelper';
import BaseHeatmap from './heatmaps/BaseHeatmap.vue';

const props = defineProps({
  entityType: {
    type: String,
    required: true,
    validator: value =>
      ['account', 'agent', 'team', 'inbox', 'label'].includes(value),
  },
  entityId: {
    type: [String, Number],
    default: null,
  },
  filters: {
    type: Object,
    default: () => ({}),
  },
  archiveContext: {
    type: Object,
    default: () => ({}),
  },
  metric: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  colorScheme: {
    type: String,
    default: 'green',
    validator: value => ['blue', 'green'].includes(value),
  },
  useUtc: {
    type: Boolean,
    default: false,
  },
});

const loading = ref(false);
const heatmapData = ref([]);
const { openArchive } = useSummaryReportArchive();

const numberOfRows = computed(() => {
  if (!props.filters.from || !props.filters.to) return 0;

  return (
    differenceInCalendarDays(
      fromUnixTime(props.filters.to),
      fromUnixTime(props.filters.from)
    ) + 1
  );
});

const archiveMetricKey = computed(() => {
  if (props.metric === 'resolutions_count') return 'resolutionsCount';
  if (
    props.metric === 'started_cycles_count' &&
    ['agent', 'team'].includes(props.entityType)
  ) {
    return 'assignmentEpisodesCount';
  }
  return 'conversationsCount';
});

const fetchHeatmap = async () => {
  if (!props.filters.from || !props.filters.to) return;
  if (props.entityType !== 'account' && !props.entityId) return;

  loading.value = true;
  try {
    const scopeParams = pickScopeFilters(props.filters);
    const { data } = await ReportsAPI.getReports({
      metric: props.metric,
      from: props.filters.from,
      to: props.filters.to,
      type: props.entityType,
      id: props.entityType === 'account' ? undefined : props.entityId,
      groupBy: 'hour',
      businessHours: props.filters.businessHours,
      timezoneOffset: props.useUtc ? 0 : undefined,
      ...scopeParams,
    });

    heatmapData.value = clampDataBetweenTimeline(
      data || [],
      props.filters.from,
      props.filters.to
    );
  } catch {
    heatmapData.value = [];
  } finally {
    loading.value = false;
  }
};

const onCellClick = ({ dateKey, hour, value }) => {
  if (!value || !props.archiveContext?.reportType) return;

  const filters = buildHourArchiveFilters({
    metricKey: archiveMetricKey.value,
    reportType: props.archiveContext.reportType,
    row:
      props.entityType === 'account' || !props.entityId
        ? null
        : { id: props.entityId },
    context: props.archiveContext,
    dateKey,
    hour,
  });

  openArchive({
    metricKey: archiveMetricKey.value,
    reportType: props.archiveContext.reportType,
    row:
      props.entityType === 'account' || !props.entityId
        ? null
        : { id: props.entityId },
    context: props.archiveContext,
    filters,
  });
};

watch(
  () => [props.filters, props.entityId, props.metric, props.entityType],
  fetchHeatmap,
  { deep: true, immediate: true }
);
</script>

<template>
  <section
    class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <h3 class="text-base font-medium text-n-slate-12">
      {{ title }}
    </h3>
    <div class="mt-4">
      <BaseHeatmap
        :heatmap-data="heatmapData"
        :number-of-rows="numberOfRows"
        :is-loading="loading"
        :color-scheme="colorScheme"
        :use-utc="useUtc"
        @cell-click="onCellClick"
      />
    </div>
  </section>
</template>
